import { tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, lastValueFrom, map, Observable, ReplaySubject, Subject } from 'rxjs';
import { UserService } from '../_core/services/user.service';
import { User } from '../_shared/models/user.model';
import { Credentials } from '../_shared/models/credentials.model';
import { SERVER_URL } from '../../environments/environment';
import { AppDetectionService } from '../_core/services/app-detection.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private authSate = new Subject<any>();
    public cast = this.authSate.asObservable();
    private loggedInObservable$ = new Subject<any>();
    public authUser: User | undefined;
    public cognitoUser: User | undefined;
    public authUserSignal = signal<User | undefined>(undefined)
    refresh$ = new BehaviorSubject<User | null>(null);
    authUser$: ReplaySubject<User | undefined> = new ReplaySubject(1)
    cognitoUser$: ReplaySubject<User | undefined> = new ReplaySubject(1)
    private isCheckingCognito = false;
    private isCheckingJWT = false;

    constructor(
        private http: HttpClient,
        public jwtHelper: JwtHelperService,
        private userService: UserService,
        private appDetectionService: AppDetectionService) { }

    public async isAuthenticated() {
        const token = localStorage.getItem('token');
        const cognitoUsername = localStorage.getItem('cognitoUsername');
        const accessToken = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');

        // console.log('isAuthenticated check:', {
        //     hasToken: !!token,
        //     hasAccessToken: !!accessToken,
        //     hasCognitoUsername: !!cognitoUsername,
        //     hasRefreshToken: !!refreshToken
        // });

        // Check if we have any tokens
        if (!token && !accessToken) {
            // console.log('No tokens found, user not authenticated');
            return false;
        }

        // For Cognito users, prioritize accessToken
        if (accessToken && cognitoUsername) {
            try {

                if (!this.isCheckingCognito && !this.cognitoUser) {
                    this.isCheckingCognito = true;
                    await this.getCognitoUser();
                    this.isCheckingCognito = false;
                } else if (this.isCheckingCognito) {
                    while (this.isCheckingCognito) {
                        await new Promise(resolve => setTimeout(resolve, 100));
                    }
                }

                const isAuthenticated = this.cognitoUser ? true : false;
                return isAuthenticated;
            } catch (error) {
                this.isCheckingCognito = false;
                return false;
            }
        }

        // For JWT token users
        if (token) {
            try {
                const decodedToken = this.jwtHelper.decodeToken(token);
                if (!decodedToken) {
                    // console.log('Token decode failed, user not authenticated');
                    return false;
                }

                if (!this.isCheckingJWT && !this.authUser) {
                    this.isCheckingJWT = true;
                    await this.getAuthUser();
                    this.isCheckingJWT = false;
                } else if (this.isCheckingJWT) {
                    while (this.isCheckingJWT) {
                        await new Promise(resolve => setTimeout(resolve, 100));
                    }
                }

                const isAuthenticated = this.authUser ? true : false;
                return isAuthenticated;

            } catch (error) {
                this.isCheckingJWT = false;
                return false;
            }
        }

        return false;
    }

    clearAuthData() {
        localStorage.removeItem('token');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('cognitoUsername');
        this.authUser = undefined;
        this.cognitoUser = undefined;
        this.authUserSignal.set(undefined);
        this.authUser$.next(undefined);
        this.cognitoUser$.next(undefined);
        this.isCheckingCognito = false;
        this.isCheckingJWT = false;
    }

    async getAuthUser() {
        if (!this.authUser) {
            this.authUser = await lastValueFrom(
                this.userService.getAuthUser()
                    .pipe(
                        map(res => res?.data as User),
                        tap(authUser => this.authUserSignal.set(authUser)),
                        tap(authUser => this.authUser$.next(authUser))
                    )
            );
        }
        return this.authUser;
    }

    async getCognitoUser() {
        if (!this.cognitoUser) {
            this.cognitoUser = await lastValueFrom(
                this.userService.getCognitoUser()
                    .pipe(
                        map(res => res?.data as User),
                        tap(cognitoUser => {
                            if (cognitoUser) {
                                this.castAuthState(cognitoUser);
                                this.authUserSignal.set(cognitoUser);
                                this.authUser$.next(cognitoUser);
                                this.authUser = cognitoUser;
                            }
                        })
                    )
            );
        }
        return this.cognitoUser;
    }

    updateAuthUser(authUser: User) {
        this.authUser = authUser;
        this.authUserSignal.set(authUser);
        this.authUser$.next(authUser);
    }

    castAuthState(data) {
        this.authSate.next(data);
    }
    /**
     * Login
     */
    async login(credentials: Credentials) {
        return this.http.post<any>('api_endpoint' + '/auth/login', { credentials })
    }

    removeRefreshToken(refreshToken: string) {
        return this.http.post<any>('api_endpoint' + '/auth/remove-refresh-token', { refreshToken });
    }

    // Signup
    signup(user, recaptchaToken) {
        return this.http.post<any>('api_endpoint' + '/auth/register', { user, recaptchaToken });
    }

    // Request code to change password
    getCode(phone) {
        return this.http.post<any>('api_endpoint' + '/auth/get-code', phone);
    }

    // Resend code
    resendCode(userId: string) {
        return this.http.post<any>('api_endpoint' + '/auth/resend-code', { userId });
    }

    // Change Password
    changePassword(forgetPasswordObject) {
        return this.http.post<any>(
            'api_endpoint' + '/auth/change-password',
            forgetPasswordObject
        );
    }

    registerDevice(pushObj) {
        return this.http.post<any>('api_endpoint' + '/auth/register-device', pushObj);
    }

    logout(deviceToken) {
        return this.http.post<any>('api_endpoint' + '/auth/logout', { deviceToken })
            .pipe(tap(() => this.clearAuthData()));
    }

    publishLoggedIn(isAuthenticated) {
        this.loggedInObservable$.next(isAuthenticated);
    }

    subscribeLoggedIn(): Observable<any> {
        return this.loggedInObservable$.asObservable();
    }

    // Create Account
    createAccount(userData: any) {
        return this.http.post<any>('api_endpoint' + '/v2/auth/register', userData);
    }

    // Sign In Account
    signIn(userData: any) {
        return this.http.post<any>('api_endpoint' + '/v2/auth/login', userData);
    }
    // Get SSO Login URL (synchronous version - for immediate use)
    getSSOLoginUrl(provider: 'Google' | 'Apple') {
        const callbackUrl = 'ioncourt://sso-callback';
        const ssoUrl = `${SERVER_URL}/v2/auth/sso-login?provider=${provider}&redirect_uri=${encodeURIComponent(callbackUrl)}`;
        return ssoUrl;
    }

    // Get SSO Login URL with app detection (asynchronous version)
    async getSSOLoginUrlWithAppDetection(provider: 'Google' | 'Apple'): Promise<string> {
        const callbackUrl = 'ioncourt://sso-callback';

        try {
            // Get app installation information
            const appInfo = await this.appDetectionService.getAppInstallationInfo();

            // Build the URL with app detection parameters
            const ssoUrl = `${SERVER_URL}/v2/auth/sso-login?provider=${provider}&redirect_uri=${encodeURIComponent(callbackUrl)}&app_installed=${appInfo.isInstalled}&platform=${appInfo.platform}&is_native=${appInfo.isNative}&detection_method=${appInfo.detectionMethod}`;


            return ssoUrl;
        } catch (error) {
            //console.error('App detection error in SSO URL generation:', error);

            // Fallback to basic URL without app detection
            const fallbackUrl = `${SERVER_URL}/v2/auth/sso-login?provider=${provider}&redirect_uri=${encodeURIComponent(callbackUrl)}&app_installed=false&platform=web&is_native=false&detection_method=error_fallback`;

            return fallbackUrl;
        }
    }

    // Exchange SSO authorization code for tokens
    exchangeSsoCode(code: string): Observable<any> {
        // Use a promise to handle the async app detection
        return new Observable(observer => {
            this.appDetectionService.getAppInstallationInfo().then(appInfo => {
                // Build the URL with app installation status and platform info
                const url = `${SERVER_URL}/v2/auth/callback?code=${code}&app_installed=${appInfo.isInstalled}&platform=${appInfo.platform}&is_native=${appInfo.isNative}&detection_method=${appInfo.detectionMethod}`;


                // Subscribe to the HTTP request and forward the results
                this.http.get<any>(url).subscribe({
                    next: (response) => observer.next(response),
                    error: (error) => observer.error(error),
                    complete: () => observer.complete()
                });
            }).catch(error => {
                // If app detection fails, proceed with default values
                const url = `${SERVER_URL}/v2/auth/callback?code=${code}&app_installed=false&platform=web&is_native=false&detection_method=error_fallback`;

                this.http.get<any>(url).subscribe({
                    next: (response) => observer.next(response),
                    error: (error) => observer.error(error),
                    complete: () => observer.complete()
                });
            });
        });
    }

    // Store SSO return URL for redirect after authentication
    setSsoReturnUrl(url: string) {
        localStorage.setItem('ssoReturnUrl', url);
    }

    getSsoReturnUrl(): string | null {
        return localStorage.getItem('ssoReturnUrl');
    }

    // Send Email Verification Code
    sendEmailVerificationCode(userData: any) {
        return this.http.post<any>('api_endpoint' + '/v3/auth/send-email-verification-code', userData);
    }

    // Verify account
    verifyCognitoUser(credentials: Credentials) {
        return this.http.post<any>('api_endpoint' + '/v2/auth/verify', credentials);
    }

    verifyManualCognitoUser(credentials: any) {
        return this.http.post<any>('api_endpoint' + '/v2/auth/verify', credentials);
    }

    verify(credentials: Credentials) {
        return this.http.post<any>('api_endpoint' + '/auth/verify', credentials);
    }

    // Resend Cognito Code
    resendCognitoCode(email: string) {
        return this.http.post<any>('api_endpoint' + '/v2/auth/resend-code', { email });
    }

    // Verify Email
    verifyEmail(userData: any) {
        return this.http.post<any>('api_endpoint' + '/v2/auth/verify-email', userData);
    }

    // Get Code
    forgotPassword(userData: any) {
        return this.http.post<any>('api_endpoint' + '/v2/auth/get-code', userData);
    }

    // Change Cognito Password
    changeCognitoPassword(userData: any) {
        const headers = new HttpHeaders().set('X-Skip-Navigation', 'true');
        return this.http.post<any>('api_endpoint' + '/v2/auth/change-password', userData, { headers });
    }

    existingEmail(email: string) {
        return this.http.post<{ exists: boolean }>('api_endpoint' + '/v2/auth/existing-email', { email });
    }

    existingPhone(phone: string) {
        return this.http.post<{ exists: boolean }>('api_endpoint' + '/v2/auth/existing-phone', { phone });
    }

    phoneVerification(email:string, phone: string){
        return this.http.post<{}>('api_endpoint' + '/v2/auth/phone-verification', {email, phone})
    }
    
    VerifyPhoneOTP(email:string, phone: string, code: string){
        return this.http.post<{}>('api_endpoint' + '/v2/auth/verify-code', {email, phone, code})
    }
    
    VerifyEmailOTP(email:string, phone: string, code: string){
        return this.http.post<{}>('api_endpoint' + '/v2/auth/verify-email-code', {email, phone, code})
    }
}
